import { Package, RegistrationCancellationList, RegistrationSession, Session } from "@/api/dtos";
import { DEFAULT_SESSION_GROUP } from "@/constants/constants";
import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import SessionGroup from "./SessionGroup";
import { useScopedField } from "@ngt/forms-core";
import { ISession } from ".";
import PackageContext from "@/contexts/PackageContext";
import Loading from "@/components/common/Loading";
import useIsAdminSection from "@/hooks/useIsAdminSection";

interface ISessionsProps {
    packageInformation: Package,
    allSessions: Session[]
}

const useSelectedSessions = (allSessions: Session[]) => {

    // create a record of all sessiongs id mapped to their required group
    const sessionRequiredGroupMap = useMemo(() => {
        return allSessions.reduce<Record<number, string>>((currentState, currentItem) => {
            currentState[currentItem.id!] = currentItem.requiredGroup ?? DEFAULT_SESSION_GROUP;

            return currentState;
        }, {});
    }, [allSessions])

    const {state: { initialValue: initialRegistrationSessions, value: registrationSessions }, context: { setValue: setSelectedSessions }} = useScopedField<RegistrationSession[]>("sessions", {value: true, initialValue: true});

    const {state: { initialValue: initialRegistrationCancellationLists, value: registrationCancellationLists }, context: { setValue: setSelectedCancellationLists }} = useScopedField<RegistrationCancellationList[]>("cancellationLists", {value: true, initialValue: true});

    const selectedSessions = useMemo(() => {
        return registrationSessions?.reduce<Record<number, [number, RegistrationSession]>>((currentState, currentItem, currentIndex) => {
            currentState[currentItem.sessionId!] = [currentIndex, currentItem]

            return currentState;
        }, {}) ?? {};
    }, [registrationSessions])

    
    const selectedCancellationLists = useMemo(() => {
        return registrationCancellationLists?.reduce<Record<number, [number, RegistrationCancellationList]>>((currentState, currentItem, currentIndex) => {
            currentState[currentItem.sessionId!] = [currentIndex, currentItem]

            return currentState;
        }, {}) ?? {};
    }, [registrationCancellationLists])

    
    const initialCancellationLists = useMemo(() => {
        return initialRegistrationCancellationLists?.reduce<Record<number, [number, RegistrationCancellationList]>>((currentState, currentItem, currentIndex) => {
            currentState[currentItem.sessionId!] = [currentIndex, currentItem]

            return currentState;
        }, {}) ?? {};
    }, [initialRegistrationCancellationLists])

    const initialSessions = useMemo(() => {
        return initialRegistrationSessions?.reduce<Record<number, [number, RegistrationSession]>>((currentState, currentItem, currentIndex) => {
            currentState[currentItem.sessionId!] = [currentIndex, currentItem]

            return currentState;
        }, {}) ?? {};
    }, [initialRegistrationSessions])

    const updateSelectedSessions = useCallback((session: Session, selectedSession?: RegistrationSession, selectedSessionIndex?: number, included?: boolean, optional?: boolean) => {
        const isOptional = session.required || optional;

        if (selectedSession && !included) {
            setSelectedSessions(current => {
                return current.filter(x => x.sessionId !== session.id);
            })
        }
        else if (!selectedSession) {
            setSelectedSessions(current => {
                let filteredSessions = current;
                
                if (isOptional) {
                    filteredSessions = current
                        .filter(x => sessionRequiredGroupMap[x.sessionId!] !== (session.requiredGroup ?? DEFAULT_SESSION_GROUP))
                }

                const newSession = new RegistrationSession({ 
                    sessionId: session.id
                });

                if (isOptional) {
                    sessionRequiredGroupMap[newSession.sessionId!] = session.requiredGroup  ?? DEFAULT_SESSION_GROUP;
                }

                if (filteredSessions.some(x => x.sessionId === session.id)) {
                    return filteredSessions;
                }

                return [...filteredSessions, newSession];
            })
        }
    }, [setSelectedSessions, sessionRequiredGroupMap])

    

    const updateSelectedCancellationLists = useCallback((session: Session, selectedCancellationList?: RegistrationCancellationList, selectedCancellationListIndex?: number/*, included?: boolean, optional?: boolean*/) => {
        //const isOptional = session.required || optional;

        if (selectedCancellationList) {
            setSelectedCancellationLists(current => {
                return current.filter(x => x.sessionId !== session.id);
            })
        }
        else if (!selectedCancellationList) {
            setSelectedCancellationLists(current => {
                let filteredCancellationLists = current;

                if (filteredCancellationLists?.some(x => x.sessionId === session.id)) {
                    return filteredCancellationLists;
                }

                const newCancellationList = initialCancellationLists?.[session.id!]?.[1] ?? new RegistrationCancellationList({ 
                    sessionId: session.id
                });

                return [...(filteredCancellationLists ?? []), newCancellationList];
            })
        }
    }, [setSelectedCancellationLists, initialCancellationLists])

    return {initialSessions, selectedSessions: selectedSessions, updateSelectedSessions, initialCancellationLists, selectedCancellationLists, updateSelectedCancellationLists };
}

const useIncludedSessions = (packageInformation: Package) => {
    return useMemo(() => {
        const sessions = packageInformation.sessions?.filter(x => !x.required); // get all that are not required.. this will aactually get more then that

        if (!sessions || sessions.length === 0) {
            return undefined;
        }

        return sessions;
    }, [packageInformation.sessions])
}


const useIncludedOptionalSessions = (packageInformation: Package) => {
    return useMemo(() => {
        const sessions = packageInformation.sessions?.filter(x => x.required);

        if (!sessions || sessions.length === 0) {
            return undefined;
        }

        return sessions.reduce<Record<string, Session[]>>((currentState, currentItem) => {
            let groupKey = currentItem.requiredGroup ?? DEFAULT_SESSION_GROUP; // set the name of the variable in the object to the name in the database or default

            if (!currentState[groupKey]) { // if and empty object
                currentState[groupKey] = []; // set the empty objects groupkey field to an array
            }

            currentState[groupKey].push(currentItem); //push items into the array

            return currentState; // bring state back up to the top of loop untill looped through all
        }, {});
    }, [packageInformation.sessions])
}

const useOptionalSessions = (sessionsIn: Session[]) => {
    const sessions = sessionsIn.filter(x => x.requiredGroup && x.eventType === 3);

    if (!sessions || sessions.length === 0) {
        return undefined;
    }

    return sessions.reduce<Record<string, Session[]>>((currentState, currentItem) => {
        let groupKey = currentItem.requiredGroup ?? DEFAULT_SESSION_GROUP; // set the name of the variable in the object to the name in the database or default

        if (!currentState[groupKey]) { // if and empty object
            currentState[groupKey] = []; // set the empty objects groupkey field to an array
        }

        currentState[groupKey].push(currentItem); //push items into the array

        return currentState; // bring state back up to the top of loop untill looped through all
    }, {});
}

const convertToSessionList = (sessions: Session[], selectedSessions: Record<number, [number, RegistrationSession]>, initialSessions: Record<number, [number, RegistrationSession]>, selectedCancellationLists: Record<number, [number, RegistrationCancellationList]>, initialCancellationLists: Record<number, [number, RegistrationCancellationList]>) => {
    return sessions.map(session => {
        const [selectedSessionIndex, selectedSession] = selectedSessions[session.id!] ?? [ undefined, undefined];
        const [initialSessionIndex, initialSession] = initialSessions[session.id!] ?? [ undefined, undefined];
        const [selectedCancellationListIndex, selectedCancellationList] = selectedCancellationLists[session.id!] ?? [ undefined, undefined];
        const [initialCancellationListIndex, initialCancellationList] = initialCancellationLists[session.id!] ?? [ undefined, undefined];

        return {
            session,
            selectedSession,
            selectedSessionIndex,
            initialSession,
            initialSessionIndex,
            selectedCancellationList,
            selectedCancellationListIndex,
            initialCancellationList,
            initialCancellationListIndex
        } as ISession
    })
}

const Sessions: FC<ISessionsProps> = ({
    packageInformation,
    allSessions
}) => {
    const {initialSessions, selectedSessions, updateSelectedSessions, initialCancellationLists, selectedCancellationLists, updateSelectedCancellationLists} = useSelectedSessions(allSessions);
    const includedSessions = useIncludedSessions(packageInformation);
    const includedOptionalSessions = useIncludedOptionalSessions(packageInformation);
    const { additionalSessions, additionalSessionsLoading } = useContext(PackageContext)

    const [optionalSessions, setOptionalSessions] = useState<Record<string, Session[]> | undefined>();

    useEffect(() => {
        if (additionalSessions) {
            setOptionalSessions(useOptionalSessions(additionalSessions));
        }
    }, [additionalSessions]);
    
    const {state: {value: sponsor}} = useScopedField<boolean>("sponsor", {value: true});
    const {state: {value: sponsorId}} = useScopedField<number>("sponsorId", {value: true});
    const {state: {value: trialCoordinator}} = useScopedField<boolean>("trialCoordinator", {value: true});
    const {state: {value: staff}} = useScopedField<boolean>("staff", {value: true});
    const {state: {value: isCap}} = useScopedField<boolean>("isCap", {value: true});

    const {state: { value: isSpeaker }} = useScopedField<boolean>("isSpeaker", { value: true });

    const isAdminSection = useIsAdminSection();

    const additionalSessionsUpdated = useMemo(() => {
        return additionalSessions?.filter(session => {
            if (sponsor && !isAdminSection && !!session.disableForSponsor) {
                return false;
            }
            
            if (sponsor && !isAdminSection && !!sponsorId && !!session.sponsorableType && (sponsorId !== session.sponsorId)) 
            {
                return false;
            }

            if (session.restrictToCap && !isCap && !isAdminSection) {
                return false;
            }

            if (!isAdminSection && session.id == 1 && !staff && !trialCoordinator) {
                return false;
            }

            if (session.requiredGroup) {
                return false;
            }

            return true;
        }) ?? [];


    }, [additionalSessions, sponsor, isAdminSection, trialCoordinator, staff]);


    if (additionalSessionsLoading)
    {
        return (
            <>
                <Loading />
            </>
        )
    }

    return (
        <>
            {
                includedSessions && (
                    <SessionGroup 
                        title="Package Includes:"
                        sessions={convertToSessionList(includedSessions, selectedSessions, initialSessions, selectedCancellationLists, initialCancellationLists)}
                        included
                        updateSelectedSessions={updateSelectedSessions}
                        updateSelectedCancellationLists={updateSelectedCancellationLists}
                    />
                )
            }
            {
                includedOptionalSessions && Object
                    .keys(includedOptionalSessions)
                    .map((key, i) => {
                        return (
                            <SessionGroup 
                                key={key}
                                title="Select between the following:"
                                sessions={convertToSessionList(includedOptionalSessions[key], selectedSessions, initialSessions, selectedCancellationLists, initialCancellationLists)}
                                included
                                optional
                                updateSelectedSessions={updateSelectedSessions}
                                updateSelectedCancellationLists={updateSelectedCancellationLists}
                            />
                        )
                    })
            }
            {
                additionalSessionsUpdated && additionalSessionsUpdated.length !== 0 && (
                    <SessionGroup 
                        title="Please select any additional sessions:"
                        sessions={convertToSessionList(additionalSessionsUpdated, selectedSessions, initialSessions, selectedCancellationLists, initialCancellationLists)}
                        updateSelectedSessions={updateSelectedSessions}
                        updateSelectedCancellationLists={updateSelectedCancellationLists}
                    />
                )
            }
            {
                optionalSessions && Object
                    .keys(optionalSessions)
                    .map((key, i) => {
                        return (
                            <SessionGroup 
                                key={key}
                                title={isSpeaker == true ? "Select between the following additional sessions:" : "Select between the following additional sessions (Optional):"}
                                sessions={convertToSessionList(optionalSessions[key], selectedSessions, initialSessions, selectedCancellationLists, initialCancellationLists)}
                                included
                                optional
                                updateSelectedSessions={updateSelectedSessions}
                                updateSelectedCancellationLists={updateSelectedCancellationLists}
                            />
                        )
                    })
            }
        </>
    );
}

export default Sessions;